import { Schedule } from "../../../models/Schedule.tsx";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../Firebase.js";
import { BetDetails, Future, PlaceWagersResult  } from "../../../models/MyBetModel.tsx";
import { Users } from "../../../models/User.tsx";
import { ClientIpInfoResponse } from "../../../models/UserActivity.tsx";
import { LeagueModel, SportModel } from "../../../models/Sport.tsx";
import { sportData } from "../SportService.tsx";
import { getLeagueConfiguredDateRange, getScheduleDateRange, isFixtureMatchup, isUpcomingPropsGame, pickPropsFixtures, PREGAME_SCHEDULE_EXTRA_DAYS } from "../../../helpers/scheduleDateRange.ts";
import { ACTIVE_SPORTSBOOKS, PRIMARY_SPORTSBOOK, SECONDARY_SPORTSBOOK, TERTIARY_SPORTSBOOK } from "../../../helpers/functions.tsx";

export async function schedule(params: { sport: string, league: string, marketId?: string | string[], id?: string | null; is_main?: boolean; }): Promise<Schedule[]> {
  const { sport, league, marketId, id, is_main } = params;
  const formattedMarketId = Array.isArray(marketId)
    ? marketId.join(",")
    : marketId;
  try {
    const scheduleRequest = await httpsCallable(functions, 'getFixturesWithOdds');
    const result = await scheduleRequest({
      sport,
      league,
      ...(formattedMarketId && { marketId: formattedMarketId }),
      id,
      is_main,
      sportsbooks: [...ACTIVE_SPORTSBOOKS],
    });

    // console.log(result)

    if (result.data) {
      // console.log(result.data)
      return (result.data as Schedule[]).filter(isFixtureMatchup);
    } else {
      throw new Error('No data');
    }
  } catch (error) {
    console.error('Error en schedule:', error);
    throw error;
  }
}

export async function scheduleLive(
  params: {
    sport?: string, league?: string, marketId?: string | string[], id?: string | null, isMain?: boolean,
    startDateAfter?: string, startDateBefore?: string, timeOffset?: string, team_id?: string, sportsbooks?: string[]
  }): Promise<Schedule[]> {
  const { sport, league, marketId, id, isMain, startDateAfter, startDateBefore, timeOffset, team_id, sportsbooks } = params;
  const formattedMarketId = Array.isArray(marketId)
    ? marketId.join(",")
    : marketId;

  try {
    const scheduleRequest = await httpsCallable(functions, 'getFixturesWithOddsToday');
    const result = await scheduleRequest({
      sport,
      league,
      startDateAfter,
      startDateBefore,
      timeOffset,
      id,
      isMain,
      team_id,
      ...(sportsbooks
        ? { sportsbooks }
        : { sportsbooks: [...ACTIVE_SPORTSBOOKS] }),
      ...(formattedMarketId && { marketId: formattedMarketId })
    });

    // console.log(result)

    if (result.data) {
      // console.log(result.data)
      return (result.data as Schedule[]).filter(isFixtureMatchup);
    } else {
      throw new Error('No data');
    }
  } catch (error) {
    console.error('Error en schedule:', error);
    throw error;
  }
}

export async function futureGames(params: { league: string, future: string | string[] }): Promise<Future[]> {
  const { league, future } = params;

  try {
    const scheduleRequest = await httpsCallable(functions, 'getFuturesWithOdds');
    const result = await scheduleRequest({
      league,
      future,
      sportsbooks: [...ACTIVE_SPORTSBOOKS],
    });

    if (result.data) {
      return result.data as Future[];
    } else {
      throw new Error('No data');
    }
  } catch (error) {
    console.error('Error en schedule:', error);
    throw error;
  }
}


interface ParlayPricer {
  market?: string,
  name?: string,
  fixture_id?: string,
  game_id?: string,
}

export type ParlayPriceEntry = {
  market: string;
  name: string;
  fixture_id: string;
};

type ParlayPriceBetLike = {
  fixture_id?: string | null;
  marketName?: string | null;
  betName?: string | null;
  odd?: {
    market?: string;
    name?: string;
    points?: number | null;
    selection_line?: string | null;
    sportsbook?: string | null;
  } | null;
};

const sameSportsbook = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const uniqueSportsbooks = (books: string[]): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const book of books) {
    const name = book.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(name);
  }
  return ordered;
};

/**
 * Parlay price order: the book on the selections, then the other active book,
 * then DraftKings. Each step is used only when the previous one has no price.
 */
export function resolveParlaySportsbooks(bets: ParlayPriceBetLike[]): string[] {
  const books = bets
    .map(bet => bet.odd?.sportsbook?.trim())
    .filter((book): book is string => Boolean(book));

  const unique = uniqueSportsbooks(books);
  const selectionBook = unique.length === 1
    ? unique[0]
    : (books[0] || PRIMARY_SPORTSBOOK);

  if (sameSportsbook(selectionBook, TERTIARY_SPORTSBOOK)) {
    return uniqueSportsbooks([selectionBook, PRIMARY_SPORTSBOOK, SECONDARY_SPORTSBOOK]);
  }

  const otherBook = sameSportsbook(selectionBook, PRIMARY_SPORTSBOOK)
    ? SECONDARY_SPORTSBOOK
    : PRIMARY_SPORTSBOOK;

  return uniqueSportsbooks([selectionBook, otherBook, TERTIARY_SPORTSBOOK]);
}

/**
 * Build showParlays entries from slip selections.
 * OpticOdds prices a parlay from market + fixture_id + name only.
 * An extra points field makes BetRivers reject lines that price without it.
 */
export function buildParlayPriceEntries(
  bets: ParlayPriceBetLike[],
): ParlayPriceEntry[] {
  return bets
    .filter(bet =>
      Boolean(
        bet.fixture_id &&
        (bet.odd?.market || bet.marketName) &&
        (bet.odd?.name || bet.betName),
      ),
    )
    .map(bet => ({
      market: String(bet.odd?.market || bet.marketName),
      name: String(bet.odd?.name || bet.betName),
      fixture_id: String(bet.fixture_id),
    }));
}

export type ParlayPriceResult =
  | { ok: true; price: number; raw: unknown }
  | { ok: false; message: string };

type ParlayBookResult = {
  error?: string;
  price?: number;
  missing_entries?: Array<{
    fixture_id?: string;
    market?: string;
    name?: string;
    price?: number | null;
  }>;
};

function formatMissingParlaySelection(entry: {
  market?: string;
  name?: string;
} | null | undefined): string {
  const name = String(entry?.name ?? "").trim();
  const market = String(entry?.market ?? "").trim();
  if (!name && !market) return "";
  if (!market) return name;
  if (!name) return market;
  if (name.toLowerCase().includes(market.toLowerCase())) return name;
  return `${name} (${market})`;
}

function formatMissingParlayList(labels: string[]): string {
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

const hasValidParlayPrice = (data?: ParlayBookResult | null): boolean => {
  if (!data || data.error) return false;
  const price = data.price;
  return typeof price === 'number' && !Number.isNaN(price) && price !== 0;
};

const findParlayBookResult = (
  response: Record<string, ParlayBookResult>,
  sportsbook: string,
): ParlayBookResult | undefined => {
  if (response[sportsbook]) return response[sportsbook];
  const match = Object.keys(response).find(key => sameSportsbook(key, sportsbook));
  return match ? response[match] : undefined;
};

function pickParlayBookResult(
  response: Record<string, ParlayBookResult>,
  preferredSportsbooks: string[],
): { name: string; data: ParlayBookResult } | null {
  for (const preferred of preferredSportsbooks) {
    const data = findParlayBookResult(response, preferred);
    if (hasValidParlayPrice(data)) return { name: preferred, data: data! };
  }
  return null;
}

export function parseParlayPriceResponse(
  response: unknown,
  sportsbooks: string[] = [PRIMARY_SPORTSBOOK],
): ParlayPriceResult {
  if (!response || typeof response !== 'object') {
    return { ok: false, message: 'Unable to calculate parlay at this time. Please try again.' };
  }

  const picked = pickParlayBookResult(
    response as Record<string, ParlayBookResult>,
    sportsbooks.length > 0 ? sportsbooks : [PRIMARY_SPORTSBOOK],
  );

  if (!picked) {
    if (sportsbooks.length >= 3) {
      return {
        ok: false,
        message: '3 different providers were tried and none of them offers this option.',
      };
    }
    return { ok: false, message: 'Unable to calculate parlay at this time. Please try again.' };
  }

  const bookData = picked.data;

  if (bookData?.error) {
    const labels = (Array.isArray(bookData.missing_entries) ? bookData.missing_entries : [])
      .map(formatMissingParlaySelection)
      .filter(Boolean);

    if (labels.length === 1) {
      return {
        ok: false,
        message: `No parlay price for ${labels[0]}. Remove this pick to continue.`,
      };
    }

    if (labels.length > 1) {
      return {
        ok: false,
        message: `No parlay price for ${formatMissingParlayList(labels)}. Remove these picks to continue.`,
      };
    }

    return { ok: false, message: bookData.error };
  }

  const price = bookData?.price;
  if (typeof price === 'number' && !Number.isNaN(price) && price !== 0) {
    return { ok: true, price, raw: bookData };
  }

  return {
    ok: false,
    message: 'Unable to calculate parlay odds for these selections. Try removing a pick or try again.',
  };
}

export async function parlayPrice(
  params: ParlayPricer[],
  sportsbooks: string | string[] = [PRIMARY_SPORTSBOOK],
): Promise<any> {
  try {
    const books = (Array.isArray(sportsbooks) ? sportsbooks : [sportsbooks])
      .map(book => book?.trim())
      .filter((book): book is string => Boolean(book));
    const sportsbooksPayload = books.length > 0 ? books : [PRIMARY_SPORTSBOOK];

    const parlayPriceRequest = await httpsCallable(functions, 'showParlays');
    const result = await parlayPriceRequest({
      entries: params,
      sportsbooks: sportsbooksPayload,
    });
    if (result.data) {
      return result.data;
    } else {
      throw new Error('No data');
    }
  } catch (error) {
    console.error('Error en schedule:', error);
    throw error;
  }
}

// export const fixAvailable = async () => {
//   const loginWithPin = httpsCallable(functions, 'fixPlayerAvailable');
//   return loginWithPin().then((result) => {
//     console.log(result.data);
//   });
// };

export async function clientIpInfo(): Promise<ClientIpInfoResponse> {
  try {
    const myFunction = httpsCallable<void, ClientIpInfoResponse>(
      functions,
      "getClientIpInfo"
    );

    const result = await myFunction();
    return result.data;
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}


function named(value?: string) {
  return String(value ?? "").trim().toLowerCase();
}

function compactName(value?: string) {
  return named(value).replace(/[^a-z0-9]/g, "");
}

function leagueMatches(league: LeagueModel, leagueName: string): boolean {
  const want = named(leagueName);
  const wantCompact = compactName(leagueName);
  if (!want) return false;
  if (named(league.name) === want || compactName(league.name) === wantCompact) return true;
  const leagueId = (league as LeagueModel & { id?: string }).id;
  return Boolean(leagueId) && (named(leagueId) === want || compactName(leagueId) === wantCompact);
}

export function findCatalogLeague(sports: SportModel[], sportName: string, leagueName: string): LeagueModel | undefined {
  const sport = sports.find(s => named(s.name) === named(sportName) || named(s.id) === named(sportName));
  const fromSport = sport?.leagues?.find(league => leagueMatches(league, leagueName));
  if (fromSport) return fromSport;

  for (const candidate of sports) {
    const found = candidate.leagues?.find(league => leagueMatches(league, leagueName));
    if (found) return found;
  }
  return undefined;
}

function sortFixturesByStart(a: Schedule, b: Schedule) {
  return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
}

export async function getFixtures(params: { sport: string, league: string }): Promise<Schedule[]> {
  const { sport, league } = params;
  const baseDate = new Date();

  try {
    const sports = await sportData();
    const leagueModel = findCatalogLeague(sports, sport, league);
    const configuredRange = getLeagueConfiguredDateRange(leagueModel, {
      baseDate,
      extraDays: PREGAME_SCHEDULE_EXTRA_DAYS,
    });
    const { timeOffset, startDateAfter, startDateBefore } = configuredRange ?? getScheduleDateRange({
      baseDate,
      extraDays: 1,
      league: leagueModel,
    });

    const scheduleRequest = await httpsCallable(functions, 'getFixtures');
    const result = await scheduleRequest({
      sport,
      league,
      timeOffset,
      startDateAfter,
      startDateBefore
    });
    if (result.data) {
      const games = (result.data as Schedule[]).filter(isFixtureMatchup);
      if (configuredRange) {
        return games.filter(game => isUpcomingPropsGame(game, baseDate)).sort(sortFixturesByStart);
      }
      return pickPropsFixtures(games, baseDate);
    } else {
      throw new Error('No data');
    }
  } catch (error) {
    console.error('Error en schedule:', error);
    throw error;
  }
}


export async function copyAgentLimitsToUsers() {
  try {
    const copyLimits = httpsCallable<
      Record<string, never>,
      {
        agentsInConfig: number;
        updated: Array<{ userName?: string; userType?: string }>;
        missing: string[];
      }
    >(functions, "copyAgentLimitsToUsers", { timeout: 540000 });

    const result = await copyLimits({});
    return result.data;
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function syncUsersPending(userDocIds?: string[]) {
  try {
    const syncPending = httpsCallable<
      { userDocIds?: string[] },
      {
        playersUpdated: number;
        playersWithPending: number;
        openBetsCounted: number;
        totalPending: number;
      }
    >(functions, "syncUsersPending");

    const result = await syncPending(
      userDocIds?.length ? { userDocIds } : {},
    );
    return result.data;
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function generateWeeklyFromSeptember() {
  try {
    const generateWeekly = httpsCallable<
      { weeks?: number; rebuildSummaries?: boolean },
      {
        weeks: string[];
        usersProcessed: number;
        docsWritten: number;
        docsCleared: number;
        summariesRebuilt: number;
        errors: string[];
      }
    >(functions, "generateWeeklyFiguresLastWeeks");

    const result = await generateWeekly({
      weeks: 3,
      rebuildSummaries: true,
    });
    return result.data;
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function dataUser(docId: string): Promise<Users> {
  const userData = httpsCallable<{ docId: string; isPublic?: boolean }, Users>(
    functions,
    "fetchUser",
  );
  const result = await userData({ docId, isPublic: false });
  return result.data as Users;
}

const LIVE_PLACE_WAGER_DELAY_MS = 2700;

const hasLiveWager = (bet?: { gameType?: string | null; selections?: { gameType?: string | null }[] } | null) => {
  const type = String(bet?.gameType ?? "").toLowerCase();
  if (type.includes("live")) return true;
  return (bet?.selections ?? []).some((selection) =>
    String(selection?.gameType ?? "").toLowerCase().includes("live")
  );
};

const waitForLiveWagerDelay = async (bets: Array<{ gameType?: string | null; selections?: { gameType?: string | null }[] } | null | undefined>) => {
  if (!bets.some((bet) => hasLiveWager(bet))) return;
  await new Promise((resolve) => setTimeout(resolve, LIVE_PLACE_WAGER_DELAY_MS));
};

export async function betRequest(bet: BetDetails, userFreePlay: boolean): Promise<BetDetails> {
  try {
    await waitForLiveWagerDelay([bet]);
    const betRequest = httpsCallable<{ bet: BetDetails, userFreePlay: boolean }, BetDetails>(
      functions,
      "placeWagers"
    );

    const result = await betRequest({ bet, userFreePlay });
    return result.data as BetDetails;
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function addMasterFields(): Promise<void> {
  try {
    const addMasterFields = httpsCallable(
      functions,
      "addMasterField"
    );
    await addMasterFields();
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function generateAnalysis(): Promise<void> {
  const generate = httpsCallable<
    { reset?: boolean },
    { done?: boolean; betsWritten?: number; remaining?: number }
  >(
    functions,
    "generateClosingLineAnalysisArchive",
    { timeout: 120000 },
  );
  let reset = true;
  try {
    for (;;) {
      const result = await generate({ reset });
      reset = false;
      const data = result.data;
      // console.info("CLA generate chunk", data);
      if (data?.done !== false) {
        return;
      }
    }
  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}

export async function placeApiAllWagers(userDocId: string, bets: BetDetails[], useFreePlay: boolean): Promise<PlaceWagersResult> {
  try {
    await waitForLiveWagerDelay(bets);
    const wager = httpsCallable<{ userDocId: string, bets: BetDetails[], useFreePlay: boolean }, PlaceWagersResult>(functions, "placeWagers");
    const result = await wager({ userDocId, bets, useFreePlay });

    return result.data;

  } catch (error) {
    console.error("Error function:", error);
    throw error;
  }
}



